"use client"

import { useCallback, useEffect, useState, type MouseEvent } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { Menu, X } from "lucide-react"

import { useLocale } from "@/lib/locale-context"
import { splitLocale } from "@/lib/seo-paths"
import { cn } from "@/lib/utils"
import { LanguageSwitcher } from "@/components/language-switcher"
import { ThemeToggle } from "@/components/theme-toggle"

const sectionIds = ["proof", "focus", "projects", "journey", "skills", "contact"] as const

export function SiteHeader() {
  const { t, href } = useLocale()
  const pathname = usePathname()
  const home = href("/")
  const isSubPage = splitLocale(pathname ?? "/").path !== "/"

  const [mobileOpen, setMobileOpen] = useState(false)
  const [active, setActive] = useState<string | null>(null)

  useEffect(() => {
    if (isSubPage) return
    const sections = sectionIds
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => Boolean(el))
    if (!sections.length) return

    const onScroll = () => {
      const line = window.innerHeight * 0.35
      let current: string | null = null
      for (const section of sections) {
        if (section.getBoundingClientRect().top <= line) current = section.id
      }
      setActive(current)
    }
    window.addEventListener("scroll", onScroll, { passive: true })
    onScroll()
    return () => window.removeEventListener("scroll", onScroll)
  }, [isSubPage])

  const label = (id: (typeof sectionIds)[number]) =>
    id === "skills" ? t.alt.skills : t.nav[id]

  const handleNavClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>, targetId: string, closeMenu = false) => {
      if (closeMenu) setMobileOpen(false)
      if (isSubPage) return
      const element = document.getElementById(targetId)
      if (!element) return
      event.preventDefault()
      element.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
        block: "start",
      })
    },
    [isSubPage]
  )

  return (
    <header className="sticky top-0 z-50 border-b border-foreground bg-background">
      <div className="shell flex h-14 items-center justify-between gap-4 md:h-16 md:gap-6">
        <Link href={home} className="t-small truncate font-semibold uppercase tracking-[0.06em] text-foreground">
          Luís Peixoto
        </Link>

        <nav className="hidden items-center gap-5 lg:flex" aria-label={t.nav.home}>
          {sectionIds.map((id, i) => {
            const isActive = !isSubPage && active === id
            return (
              <a
                key={id}
                href={isSubPage ? `${home}#${id}` : `#${id}`}
                onClick={(event) => handleNavClick(event, id)}
                aria-current={isActive ? "true" : undefined}
                className={cn(
                  "nav-link t-small whitespace-nowrap transition-colors",
                  isActive ? "text-accent" : "text-muted-foreground hover:text-foreground"
                )}
              >
                <span className="tabular-nums">{`0${i + 1}`}</span>
                <span className="ml-2">{label(id)}</span>
              </a>
            )
          })}
        </nav>

        <div className="flex items-center gap-5">
          <div className="hidden items-center gap-4 lg:flex">
            <LanguageSwitcher />
            <ThemeToggle />
          </div>
          <Link
            href="/cv"
            className="btn btn-solid t-small flex h-9 items-center bg-foreground px-4 font-medium uppercase tracking-[0.06em] text-background"
          >
            {t.nav.cv}
          </Link>
          <button
            type="button"
            className="-mr-2 flex h-11 w-11 items-center justify-center text-muted-foreground transition-colors hover:text-foreground md:hidden"
            onClick={() => setMobileOpen((open) => !open)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <div
        className={cn(
          "grid border-t border-border transition-[grid-template-rows] duration-200 md:hidden",
          mobileOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr] border-t-0"
        )}
      >
        <div className="overflow-hidden">
          <div className="shell py-2">
            {sectionIds.map((id, i) => (
              <a
                key={id}
                href={isSubPage ? `${home}#${id}` : `#${id}`}
                onClick={(event) => handleNavClick(event, id, true)}
                className="t-body flex gap-3 border-b border-border py-3.5 text-foreground last:border-b-0"
              >
                <span className="tabular-nums text-accent">{`0${i + 1}`}</span>
                {label(id)}
              </a>
            ))}
            <div className="flex items-center gap-5 py-4">
              <LanguageSwitcher />
              <ThemeToggle />
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}
